import { APPLICATION_ACTION_STATE, APPLICATION_MANIFEST_SOURCE_TYPE, APPLICATION_SOURCE_TYPE, EPINIO_PRODUCT_NAME } from '../types';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import Resource from '@shell/plugins/dashboard-store/resource-class';
import { set } from '@shell/utils/object'; // Vue 3-compatible reactivity setter

export const APPLICATION_ACTION_TYPE = {
  CREATE_NS:           'create_namespace',
  CREATE:              'create',
  UPDATE_SOURCE:       'updateSource',
  GIT_FETCH:           'gitFetch',
  UPLOAD:              'upload',
  BIND_CONFIGURATIONS: 'bind_configurations',
  BIND_SERVICES:       'bind_services',
  BUILD:               'build',
  DEPLOY:              'deploy',
};

export default class ApplicationActionResource extends Resource {
  run = true;
  state = APPLICATION_ACTION_STATE.PENDING;

  get name() {
    return this.t(`epinio.applications.action.${ this.action }.label`);
  }

  get description() {
    return this.t(`epinio.applications.action.${ this.action }.description`);
  }

  get stateObj() {
    switch (this.state) {
      case APPLICATION_ACTION_STATE.SUCCESS:
        return { name: 'succeeded', error: false, transitioning: false };
      case APPLICATION_ACTION_STATE.RUNNING:
        return { name: 'pending', error: false, transitioning: true };
      case APPLICATION_ACTION_STATE.FAIL:
        return {
          name: 'fail', error: true, transitioning: false, message: this.stateMessage
        };
      case APPLICATION_ACTION_STATE.PENDING:
      default:
        return { name: 'pending', error: false, transitioning: false };
    }
  }

  async execute(params) {
    try {
      set(this, 'state', APPLICATION_ACTION_STATE.RUNNING);

      await this.innerExecute(params);

      set(this, 'state', APPLICATION_ACTION_STATE.SUCCESS);
      set(this, 'run', false);
    } catch (err) {
      set(this, 'state', APPLICATION_ACTION_STATE.FAIL);
      const errorItem = epinioExceptionToErrorsArray(err)[0];
      const message = typeof errorItem === 'string' ? errorItem : errorItem?.message || JSON.stringify(errorItem);
      set(this, 'stateMessage', message);
      throw err;
    }
  }

  async innerExecute(params) {
    switch (this.action) {
      case APPLICATION_ACTION_TYPE.CREATE_NS:           return this.createNamespace(params);
      case APPLICATION_ACTION_TYPE.CREATE:              return this.create(params);
      case APPLICATION_ACTION_TYPE.UPDATE_SOURCE:       return this.updateSource();
      case APPLICATION_ACTION_TYPE.BIND_CONFIGURATIONS: return this.bindConfigurations(params);
      case APPLICATION_ACTION_TYPE.BIND_SERVICES:       return this.bindServices(params);
      case APPLICATION_ACTION_TYPE.GIT_FETCH:           return this.gitFetch(params);
      case APPLICATION_ACTION_TYPE.UPLOAD:              return this.upload(params);
      case APPLICATION_ACTION_TYPE.BUILD:               return this.build(params);
      case APPLICATION_ACTION_TYPE.DEPLOY:              return this.deploy(params);
    }
  }

  async createNamespace() {
    const ns = await this.$dispatch(`${ EPINIO_PRODUCT_NAME }/createNamespace`, {
      name: this.application.meta.namespace
    }, { root: true });

    await ns.create();
  }

  async create() {
    await this.application.create();
  }

  async bindConfigurations() {
    await this.application.updateConfigurations([], this.bindings.configurations);
  }

  async bindServices() {
    await this.application.updateServices([], this.bindings.services);
  }

  async upload({ source }) {
    const tarball = source?.archive?.tarball;

    // Without this the API is handed an empty form field and answers with a
    // "no such file" 400 that says nothing about what the user should do.
    if (!tarball) {
      throw new Error(this.t('epinio.applications.action.upload.missingSource'));
    }

    await this.application.storeArchive(tarball);
  }

  async gitFetch({ source }) {
    const rev = source.git?.commit || source.gitUrl?.branch;
    const url = source.git?.url || source.gitUrl?.url;
    const gitconfig = source.git?.gitconfig || source.gitUrl?.gitconfig || '';

    return await this.application.gitFetch(url, rev, gitconfig);
  }

  async build({ source }) {
    const isContainer = source.type === APPLICATION_SOURCE_TYPE.CONTAINER_URL;
    const image = isContainer ? source.container.url : undefined;
    const blobUid = isContainer ? undefined : this.application.buildCache.store?.blobUid;
    const builderImage = isContainer ? undefined : source.builderImage;
    const buildMode = isContainer ? undefined : source.buildMode;
    const dockerfilePath = isContainer ? undefined : source.dockerfilePath;

    await this.application.waitAsyncBuildPhase({
      blobUid,
      builderImage,
      buildMode,
      dockerfilePath,
      image,
      origin: this.createDeployOrigin(source),
      isContainer
    });
  }

  async updateSource() {
    await this.application.update({ restart: false });
  }

  async deploy({ source }) {
    const isContainer = source.type === APPLICATION_SOURCE_TYPE.CONTAINER_URL;
    const image = isContainer ? source.container.url : undefined;

    const blobUid = isContainer ? undefined : this.application.buildCache.store?.blobUid;
    const builderImage = isContainer ? undefined : source.builderImage;
    const buildMode = isContainer ? undefined : source.buildMode;
    const dockerfilePath = isContainer ? undefined : source.dockerfilePath;

    await this.application.waitAsyncDeployPhase({
      blobUid,
      builderImage,
      buildMode,
      dockerfilePath,
      image,
      origin: this.createDeployOrigin(source),
      isContainer
    });
    this.application.showAppLog();
  }

  createDeployOrigin(source) {
    switch (source.type) {
      case APPLICATION_SOURCE_TYPE.ARCHIVE:
        return {
          kind: APPLICATION_MANIFEST_SOURCE_TYPE.PATH,
          archive: true,
          path: source.archive.fileName,
        };
      case APPLICATION_SOURCE_TYPE.FOLDER:
        return {
          kind: APPLICATION_MANIFEST_SOURCE_TYPE.PATH,
          path: source.archive.fileName,
        };
      case APPLICATION_SOURCE_TYPE.CONTAINER_URL:
        return {
          kind: APPLICATION_MANIFEST_SOURCE_TYPE.CONTAINER,
          container: source.container.url,
        };
      case APPLICATION_SOURCE_TYPE.GIT_URL:
        return {
          kind: APPLICATION_MANIFEST_SOURCE_TYPE.GIT,
          git: {
            revision: source.gitUrl.branch,
            repository: source.gitUrl.url,
            gitconfig: source.gitUrl.gitconfig,
          },
        };
      case APPLICATION_SOURCE_TYPE.GIT_HUB:
      case APPLICATION_SOURCE_TYPE.GIT_LAB:
        return {
          kind: APPLICATION_MANIFEST_SOURCE_TYPE.GIT,
          git: {
            revision: source.git.commit,
            repository: source.git.url,
            branch: source.git.branch?.name,
            provider: source.type,
            gitconfig: source.git.gitconfig,
          },
        };
    }
  }
}
